import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { IUserRole, RoleType } from '@account-users/user/models/UserRole';
import * as fromUser from '@account-users/user/state';
import * as fromAccount from '@account-users/account/state';
import * as accountsActions from '@account-users/account/state/accounts/accounts.actions';
import { Store, select } from '@ngrx/store';

import { PermissionsState } from '@account-users/user/state/permissions/permissions.reducer';
import { LoadPermissions } from '@account-users/user/state/permissions/permissions.actions';
import {
  LicensePermission,
  UsersLicensPermissions,
} from '@account-users/account/models/LicensePermissionList';
import { Permission } from '@account-users/account/models/Permission';
import { LoadRoles } from '@account-users/user/state/roles/roles.actions';
import { License } from '@account-users/account/models/License';
import { LoadAllUserLicenses } from '@account-users/user/state/user-licenses/user-licenses.actions';
import {
  LoadUsers,
  LoadAccountUsers,
  ResetAccountUsers,
  SetBulkEdit,
  ResetBulkEdit,
  SelectUser,
  SelectAllUsers,
  ResetSelectedUsers,
  DownloadAccountUserCSV,
} from '@account-users/user/state/users/users.actions';
import { ISearchUser, ITabItem } from '@account-users/user/models/ISearchUser';
import { trigger, transition, style, animate } from '@angular/animations';
import { Router, ActivatedRoute } from '@angular/router';
import { AccountCategory } from '@account-users/account/models/AccountCategory';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmBulkEditModalComponent } from '@account-users/user/components/confirm-bulk-edit-modal/confirm-bulk-edit-modal.component';
import { AuthService } from '@account-users/auth/auth.service';
import { removeItemFromArray } from '@shared';

@Component({
  selector: 'app-user-search-page',
  templateUrl: './user-search-page.component.html',
  styleUrls: ['./user-search-page.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
  ],
})
export class UserSearchPageComponent implements OnInit, OnDestroy {
  @ViewChild('bulkEditList') bulkEditList: ElementRef;
  isSearching$: Observable<boolean> = this.store.pipe(
    select(fromUser.getUserLoading),
  );
  users$: Observable<ISearchUser[]> = this.store.pipe(
    select(fromUser.getAllUsers),
  );
  permissions$: Observable<LicensePermission[]> = this.store.pipe(
    select(fromUser.getAllUserPermissions),
  );
  userPermissions$: Observable<Permission[]> = this.store.pipe(
    select(fromUser.getUserPermissions),
  );
  userRoles$: Observable<IUserRole[]> = this.store.pipe(
    select(fromUser.getUserRoles),
  );
  licenses$: Observable<License[]> = this.store.pipe(
    select(fromUser.getAllUserLicenses),
  );
  loadingFilter$: Observable<boolean> = this.store.pipe(
    select(fromUser.getUserLicenceLoading),
  );
  permissionUserCount$: Observable<number> = this.store.pipe(
    select(fromUser.getNumberOfUsers),
  );
  accountCategories$: Observable<AccountCategory[]> = this.store.pipe(
    select(fromAccount.getAccountCategories),
  );
  selectedUsers$: Observable<ISearchUser[]> = this.store.pipe(
    select(fromUser.getSelectedUsers),
  );

  modalRef: NgbModalRef;
  tabs: ITabItem[] = [
    { id: 'users', value: 'Users' },
    { id: 'account-users', value: 'Account-Users' },
  ];
  activeTab = 'users';
  editUsers = false;

  _currentPermissionFilters: string[] = [];
  _currentPermission: UsersLicensPermissions[] = [];
  roleTypes = RoleType;
  selectedUsers: ISearchUser[] = [];

  tab = '';
  showLicenceFilter = false;
  query = '';
  roleString = '';
  categoryString = '';
  permissionString = '';
  licenseString = '';
  debounceIndex = 0;

  permissions = [];

  private readonly subscriptions = new Subscription();
  private destroy$: Subject<void> = new Subject<void>();
  private filterDebouncer$: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private auth: AuthService,
    private store: Store<PermissionsState>,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.store.dispatch(new LoadAllUserLicenses());
    this.store.dispatch(new LoadPermissions());
    this.store.dispatch(new accountsActions.LoadAccountCategories());
    this.store.dispatch(new LoadRoles());
    this.setupFilterDebouncer();
    this.subscriptions.add(
      this.selectedUsers$.subscribe((users) => (this.selectedUsers = users)),
    );
    this.subscriptions.add(
      this.permissions$.subscribe(
        (permissions) => (this.permissions = permissions),
      ),
    );
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        this.activeTab = params['t'] ? params['t'] : 'users';
        this.query = params['q'] ? params['q'] : '';
        this.roleString = params['r'];
        this.categoryString = params['c'];
        this.showLicenceFilter = params['s'] === 'true' ? true : false;
        this.licenseString = params['l'];
        this.permissionString = params['p'];
      }),
    );

    this.activeTab === 'users' ? this.searchUsers() : this.searchAccountUsers();
  }

  setParams(params) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }

  get categoryArray() {
    return this.categoryString && this.categoryString.length
      ? this.categoryString.split('+')
      : [];
  }

  get roleArray() {
    return this.roleString && this.roleString.length
      ? this.roleString.split('+')
      : [];
  }

  get licenseArray() {
    return this.licenseString && this.licenseString.length
      ? this.licenseString.split('+')
      : [];
  }

  get permissionArray() {
    return this.permissionString && this.permissionString.length
      ? this.permissionString.split('+')
      : [];
  }

  ngOnDestroy() {
    this.store.dispatch(new ResetSelectedUsers());
    this.store.dispatch(new ResetBulkEdit());
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.filterDebouncer$.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  get boUserCanRead() {
    const boUser = this.auth.profile;
    return boUser.permissions.includes(Permission.CanRead);
  }

  resetBulk() {
    this.store.dispatch(new ResetAccountUsers());
    this.store.dispatch(new ResetBulkEdit());
  }

  changeTab(id: string) {
    if (this.activeTab !== id) {
      this.resetBulk();
      this.activeTab = id;
      this.filterDebouncer$.next(id);
      this.setParams({ t: id, r: '', c: '', l: '', p: '', s: false });
    }
  }

  search(query: string) {
    this.setParams({ q: query });
    this.filterDebouncer$.next(query);
  }

  changeRoles(roles: string[]) {
    const roleString = roles.join('+');
    this.setParams({ r: roleString });
    this.filterDebouncer$.next(roles);
  }

  changeAccountCategory(categories: string[]) {
    const categoryString = categories.join('+');
    this.setParams({ c: categoryString });
    this.debounceIndex++;
    this.filterDebouncer$.next(categoryString + this.debounceIndex);
  }

  showHideLicensesFilter(show): void {
    this.showLicenceFilter = show;
    if (show) {
      this.setParams({ l: '', p: '', s: show });
    } else {
      this.setParams({ s: show });
    }
  }

  searchUsers() {
    this.store.dispatch(
      new LoadUsers({ query: this.query, roles: this.roleArray }),
    );
  }

  searchAccountUsers() {
    this.store.dispatch(new ResetSelectedUsers());
    this.store.dispatch(
      new LoadAccountUsers({
        query: this.query,
        roles: this.roleArray,
        accountCategories: this.categoryArray,
        permissions: this.permissionArray,
      }),
    );
  }

  setupFilterDebouncer(): void {
    this.filterDebouncer$
      .pipe(debounceTime(600), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((p) => {
        this.activeTab === 'users'
          ? this.searchUsers()
          : this.searchAccountUsers();
      });
  }

  selectPermission({ code, permission, toggleOn }) {
    if (permission) {
      this.permissionString = toggleOn
        ? [...this.permissionArray, `${code}:${permission}`].join('+')
        : removeItemFromArray(
            this.permissionArray,
            `${code}:${permission}`,
          ).join('+');
      this.setParams({ p: this.permissionString });
      this.filterDebouncer$.next({ code, permission, toggleOn });
    } else {
      const array = [];
      this.permissionArray.forEach((p) => {
        if (p.split(':')[0] !== code) {
          array.push(p);
        }
      });
      this.permissionString = array.join('+');
      this.licenseString = toggleOn
        ? [...this.licenseArray, code].join('+')
        : removeItemFromArray([...this.licenseArray], code).join('+');
      this.setParams({ p: this.permissionString, l: this.licenseString });
      if (!toggleOn) {
        this.filterDebouncer$.next({ code, permission, toggleOn });
      }
    }
  }

  selectUser(user: ISearchUser) {
    this.store.dispatch(new SelectUser({ user }));
  }

  selectAllUsers(users: ISearchUser[]) {
    this.store.dispatch(new SelectAllUsers({ users }));
  }

  editSelectedUsers() {
    if (this.permissionArray.length) {
      this.editUsers = true;
      this.scrollTo('bulkEditList');
    }
  }

  scrollTo(element: string) {
    const y =
      this[element].nativeElement.getBoundingClientRect().top +
      window.pageYOffset -
      60;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  approveChangePermissions() {
    this.store.dispatch(
      new SetBulkEdit({
        users: this.selectedUsers,
        licenses: this.getPermissions(),
      }),
    );
    this.modalRef = this.modalService.open(ConfirmBulkEditModalComponent, {
      windowClass: 'custom-width',
      backdrop: 'static',
      keyboard: false,
    });
    this.modalRef.result.then(() => {
      this.searchAccountUsers();
      this.editUsers = false;
      this.resetBulk();
    });
  }

  exportAccountUsers(allAccountUsers: boolean) {
    this.store.dispatch(new DownloadAccountUserCSV(allAccountUsers));
  }

  goTo(user: ISearchUser) {
    this.activeTab === 'account-users'
      ? this.router.navigate([
          `au/accounts/${user.accountId}/users/${user.userId}`,
        ])
      : this.router.navigate([`users/${user.userId}`]);
  }

  getPermissions() {
    let permissions = [];
    this.permissionArray.forEach((p) => {
      const [code, ...codePermission] = p.split(':');
      const permission = codePermission.join(':');
      const exists = permissions.findIndex((c) => c.code === code);
      exists >= 0
        ? (permissions[exists].permissions = [
            ...permissions[exists].permissions,
            permission,
          ])
        : (permissions = [
            ...permissions,
            { code: code, permissions: [permission] },
          ]);
    });
    return permissions;
  }
}
